<script lang="ts">
  import ProductListLayout from "~/components/collection/ProductListLayout/ProductListLayout.svelte";
  import { JsonLd } from "svelte-meta-tags";
  import { buildCollectionPageSchema } from "~/lib/structured-data";
  import { SORT_OPTIONS } from "~/lib/bloomreach/config";
  import { createPlpListing } from "~/lib/listings/create-plp-listing.svelte";
  import { CARD_SELECTOR } from "~/components/collection/ProductCardGrid/ProductCardGrid.svelte";

  interface Props {
    accountId?: string;
    domainKey?: string;
    collectionKey?: string;
    rows?: number;
    collectionCount?: number;
    collectionTitle?: string;
    collectionDescription?: string;
    collectionUrl?: string;
    shopUrl?: string;
  }

  let {
    accountId,
    domainKey,
    collectionKey,
    rows,
    collectionCount,
    collectionTitle,
    collectionDescription,
    collectionUrl,
    shopUrl,
  }: Props = $props();

  const resolvedRows = $derived(rows ?? 48);
  const skeletonCount = $derived(
    collectionCount != null && collectionCount > 0
      ? Math.min(collectionCount, resolvedRows)
      : resolvedRows,
  );

  // Props are static island config injected once from Liquid. Pass as getters so
  // Svelte's reactive tracking is satisfied while preserving the initial values.
  const plpListing = createPlpListing({
    cardSelector: CARD_SELECTOR,
    get accountId() {
      return accountId;
    },
    get domainKey() {
      return domainKey;
    },
    get collectionKey() {
      return collectionKey;
    },
    get rows() {
      return rows;
    },
  });
  const listing = plpListing.listing;

  const page0Docs = $derived(listing.query.data?.pages[0]?.response?.docs);

  const plpSchema = $derived.by(() => {
    if (!page0Docs?.length || !collectionTitle || !collectionUrl) return null;
    return buildCollectionPageSchema({
      name: collectionTitle,
      description: collectionDescription,
      url: collectionUrl,
      shopUrl: shopUrl ?? "",
      docs: page0Docs,
      activeSort: listing.activeSort,
      currency: window.DUSK?.currency ?? "GBP",
    });
  });
</script>

{#if plpSchema}
  <JsonLd schema={plpSchema} />
{/if}

<ProductListLayout
  {skeletonCount}
  filters={listing.filters}
  sortOptions={SORT_OPTIONS}
  products={listing.products}
  loading={listing.query.isPending}
  error={listing.isInitialError}
  hasMore={listing.query.hasNextPage}
  loadingMore={listing.query.isFetchingNextPage}
  loadMoreError={listing.isLoadMoreError}
  onLoadMore={plpListing.handleLoadMore}
  resultCount={listing.resultCount}
  bind:activeFilters={listing.pendingFilters}
  sort={listing.activeSort}
  onSortChange={plpListing.handleSortChange}
  onReset={plpListing.handleReset}
/>
